import { useEffect, useState } from 'react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import GemsIcon from 'src/assets/icons/Store/GemsSecondary.svg';
import type { StoreInitialState } from 'src/redux/types/store';
import RazorPayIcon from 'src/assets/icons/Razorpay.svg';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import { format } from 'date-fns';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import {
  firstChargeDate,
  freeCycleWord,
  proRecurringTerms,
} from 'src/helpers/subscription';
import type { CouponValidationStatus } from 'src/pages/employerPremium/types';
import { useAppDispatch, useAppSelector } from 'src/redux';
import type { AddItemToCartBody } from 'src/openapi';
import { addToCart } from 'src/redux/actions/store';
import { getFormattedErr } from 'src/helpers';
import { getGemsDiscountCap } from 'src/helpers/utils';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CouponAppliedIcon from 'src/assets/icons/CouponAppliedIcon.svg';
import { InheritedSpan } from 'src/pages/contribute/styles';
import FreeMonthTimeline, { FreeMonthNextStep } from './FreeMonthTimeline';
import {
  CartCheckoutContainer,
  CouponDiscountTag,
  CouponInput,
  CouponInputContainer,
  CheckoutCta,
} from '../../styles/Checkout.styles';
import { RazorpayMark, SectionEyebrow, SectionTitle } from '../../styles';
import GemsDiscountToggle from './GemsDiscountToggle';

interface RescueCodeAppliedProps {
  code: string;
}

const RescueCodeApplied = ({ code }: RescueCodeAppliedProps) => (
  <Stack sx={{ gap: '2px', my: '12px' }}>
    <CouponInputContainer>
      <Box sx={{ display: 'flex', gap: '8px', width: '100%' }}>
        <Box component="img" src={CouponAppliedIcon} />
        <Typography sx={{ color: 'text.primary', fontWeight: '600' }}>
          {code}{' '}
          <InheritedSpan sx={{ fontWeight: '400' }}>applied</InheritedSpan>
        </Typography>
      </Box>
    </CouponInputContainer>
    <CouponDiscountTag>
      <Typography sx={{ color: 'success.main', fontSize: '12px' }}>
        Your special price, every month. No code to enter.
      </Typography>
    </CouponDiscountTag>
  </Stack>
);

const PricingDetails = ({
  store,
  onContinue,
  variant,
  disabled,
  insufficientGems,
  isGemsOnly,
  fullWidth,
  userGems,
  hideHeader,
}: {
  store: StoreInitialState;
  // useGems reflects the "use my gems" checkbox below — the caller reads it
  // to pass CartCheckoutBody.use_gems through to the cartCheckout thunk.
  onContinue?: (useGems: boolean) => void;
  variant: 'cart' | 'checkout';
  disabled?: boolean;
  insufficientGems?: boolean;
  isGemsOnly?: boolean;
  fullWidth?: boolean;
  // The buyer's gems balance — only meaningful for variant="checkout", where
  // it can be applied as a discount (1 gem = ₹1) against the currency total.
  userGems?: number;
  // For a caller that shows the "Order summary" heading itself, somewhere this
  // component cannot put it — the course dialog puts it at the top of the sheet.
  hideHeader?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { cartDetails } = useAppSelector((state) => state.store);
  const [couponCode, setCouponCode] = useState<string>('');
  const [couponValidationStatus, setCouponValidationStatus] =
    useState<CouponValidationStatus | null>(null);
  const [couponFailureReason, setCouponFailureReason] = useState<null | string>(
    null,
  );
  const [useGems, setUseGems] = useState(false);

  // A cash line for an is_recurring item makes this cart an autopay mandate.
  // Coupons and store offers ARE honoured: the mandate is created at the
  // discounted payable, so the buyer keeps that price for its whole term. Only
  // the gems discount is refused server-side — it is a one-off offset against a
  // single payment and cannot recur — so only that control is hidden here
  // rather than offered and then rejected at the gateway.
  const recurringItem = (store.cartDetails?.cart ?? []).find(
    (line) =>
      line.item?.is_recurring && (line.total_amount_in_currency ?? 0) > 0,
  )?.item;
  const isMandateCart = Boolean(recurringItem);
  // A price the assistant gave is already on the account: the checkout says
  // it is applied instead of asking for its code (typing it would fail -- it
  // is not a coupon).
  const rescueCode = recurringItem?.offer_rescue_code;
  const mandateFreeCycles = recurringItem?.subscription_free_cycles ?? 0;
  // The buyer's intro offer can give days in place of the free month.
  const { freeDays: mandateFreeDays } = proRecurringTerms(recurringItem);
  // Charges + free months. subscription_total_count counts invoices only, so on
  // its own it understates the commitment by the free period -- unless that
  // period is days, which cover no month.
  const mandateMonthsCovered =
    (recurringItem?.subscription_total_count ?? 0) +
    (mandateFreeDays ? 0 : mandateFreeCycles);

  // A cart of one — which every direct purchase is — says what is being bought
  // instead of "Cart Subtotal": the buyer is confirming an item, not a basket.
  const cartLines = store.cartDetails?.cart ?? [];
  const soleLine = cartLines.length === 1 ? cartLines[0] : undefined;
  const soleQuantity = soleLine?.quantity ?? 1;
  const subtotalLabel = soleLine?.item?.name
    ? `${soleLine.item.name}${soleQuantity > 1 ? ` × ${soleQuantity}` : ''}`
    : 'Cart Subtotal (Rs)';

  const netCurrency = store.cartDetails?.net_value_in_currency || 0;
  // What the item costs at list, so a discounted mandate can promise the buyer
  // that their lower price is the recurring one rather than a first-cycle tease.
  const mandateListTotal = recurringItem?.gross_price_in_currency ?? 0;
  const hasMandateDiscount =
    isMandateCart && mandateListTotal > 0 && netCurrency < mandateListTotal;
  // Calendar months (or the offer's days), matching the backend's
  // relativedelta — 30-day arithmetic would print a date Razorpay is not going
  // to charge on.
  const firstChargeLabel = format(
    firstChargeDate(mandateFreeCycles, mandateFreeDays),
    'd MMM yyyy',
  );
  // A free first month at checkout reads as one: nothing today, then the
  // price and when, then cancel -- not an invoice whose biggest figure is the
  // monthly price the buyer is not paying today.
  const freeMonthMode =
    variant === 'checkout' && isMandateCart && mandateFreeCycles > 0;
  const netGems = store.cartDetails?.net_value_in_gems || 0;
  // Rows worth showing. A cart with no gems leg and no discount was printing
  // four rows of zeroes above the total, which reads as clutter the buyer has
  // to scan past to find what they owe.
  const subtotalGems = store.cartDetails?.total_mrp_gems || 0;
  const discountCurrency = store.cartDetails?.total_discount_currency || 0;
  const discountGems = store.cartDetails?.total_discount_gems || 0;
  const gemsInPlay = subtotalGems > 0 || netGems > 0 || discountGems > 0;
  const gemsToApply =
    variant === 'checkout'
      ? getGemsDiscountCap(userGems, netCurrency, netGems)
      : 0;
  const displayedNetCurrency =
    useGems && gemsToApply > 0 ? netCurrency - gemsToApply : netCurrency;
  const effectiveGemsOnly =
    isGemsOnly || (useGems && gemsToApply > 0 && displayedNetCurrency <= 0);

  useEffect(() => {
    setCouponCode(cartDetails?.coupon_code ?? '');
    setCouponValidationStatus(cartDetails?.coupon_code ? 'valid' : null);
  }, [cartDetails]);

  const onApplyRemoveCoupon = async (remove: boolean) => {
    if (!cartDetails?.cart) {
      return;
    }

    const cart = cartDetails?.cart;

    const items: AddItemToCartBody[] = (cart ?? []).map((item) => ({
      item_id: item.item_id,
      quantity: item.quantity,
      item_payment_mode: item.total_amount_in_currency ? 'Currency' : 'Gems',
    }));

    const res = await dispatch(
      remove
        ? addToCart({ items, couponCode: null })
        : addToCart({ items, couponCode }),
    );

    if (addToCart.rejected.match(res)) {
      return;
    }

    if (res.payload.coupon_failure_reason === 'no_coupon_applied') {
      setCouponValidationStatus(null);
      setCouponFailureReason(null);
      setCouponCode('');
    } else if (res.payload.coupon_valid) {
      setCouponValidationStatus('valid');
    } else if (!res.payload.coupon_valid) {
      if (res.payload.coupon_failure_reason?.includes('expired')) {
        setCouponValidationStatus('expired');
      } else {
        setCouponValidationStatus('invalid');
      }
      setCouponFailureReason(
        res.payload.coupon_failure_reason
          ? getFormattedErr(res.payload.coupon_failure_reason)
          : null,
      );
    }
  };

  return (
    <Stack sx={{ width: fullWidth ? '100%' : { sm: '100%', lg: '35%' } }}>
      {!hideHeader && (
        <Stack sx={{ gap: '4px', mb: '12px' }}>
          <SectionEyebrow>
            <ReceiptLongRoundedIcon sx={{ fontSize: 13 }} />
            Order summary
          </SectionEyebrow>
          <SectionTitle sx={{ fontSize: '24px' }}>Cart Total</SectionTitle>
        </Stack>
      )}
      <CartCheckoutContainer>
        {freeMonthMode ? (
          <>
            <FreeMonthTimeline
              freeCycles={mandateFreeCycles}
              freeDays={mandateFreeDays}
              price={netCurrency}
              listPrice={mandateListTotal}
              firstChargeLabel={firstChargeLabel}
              monthsCovered={mandateMonthsCovered}
            />
            {rescueCode && <RescueCodeApplied code={rescueCode} />}
          </>
        ) : (
          <>
            <Stack
              sx={{ flexDirection: 'row', justifyContent: 'space-between' }}
            >
              <Typography sx={{ fontWeight: '400', fontSize: '16px' }}>
                {subtotalLabel}
              </Typography>
              <Typography sx={{ fontSize: '16px', color: 'text.primary' }}>
                {`₹${store.cartDetails?.total_mrp_currency || 0}`}
              </Typography>
            </Stack>
            {gemsInPlay && (
              <Stack
                sx={{ flexDirection: 'row', justifyContent: 'space-between' }}
              >
                <Typography sx={{ fontWeight: '400', fontSize: '16px' }}>
                  Cart Subtotal (Gems)
                </Typography>
                <Stack
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <img src={GemsIcon} height="22px" />
                  <Typography sx={{ fontSize: '16px', color: 'text.primary' }}>
                    {store.cartDetails?.total_mrp_gems || 0}
                  </Typography>
                </Stack>
              </Stack>
            )}
            {discountCurrency > 0 && (
              <Stack
                sx={{ flexDirection: 'row', justifyContent: 'space-between' }}
              >
                <Typography sx={{ fontWeight: '400', fontSize: '16px' }}>
                  Discount (Rs)
                </Typography>
                <Typography sx={{ fontSize: '16px', color: '#388E3C' }}>
                  {`-₹${discountCurrency}`}
                </Typography>
              </Stack>
            )}
            {discountGems > 0 && (
              <Stack
                sx={{ flexDirection: 'row', justifyContent: 'space-between' }}
              >
                <Typography sx={{ fontWeight: '400', fontSize: '16px' }}>
                  Discount (Gems)
                </Typography>
                <Stack
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: '16px', color: 'success.main' }}>
                    -
                  </Typography>
                  <img src={GemsIcon} height="22px" />
                  <Typography sx={{ fontSize: '16px', color: 'success.main' }}>
                    {`${discountGems}`}
                  </Typography>
                </Stack>
              </Stack>
            )}
            {variant === 'checkout' && rescueCode && (
              <RescueCodeApplied code={rescueCode} />
            )}
            {variant === 'checkout' && !rescueCode && (
              <Stack sx={{ gap: '2px', my: '12px' }}>
                <Typography sx={{ color: 'text.primary', fontWeight: '400' }}>
                  Coupon code
                </Typography>
                <CouponInputContainer>
                  {couponValidationStatus === 'valid' ? (
                    <Box sx={{ display: 'flex', gap: '8px', width: '100%' }}>
                      <Box component="img" src={CouponAppliedIcon} />
                      <Typography
                        sx={{ color: 'text.primary', fontWeight: '600' }}
                      >
                        {couponCode}{' '}
                        <InheritedSpan sx={{ fontWeight: '400' }}>
                          applied
                        </InheritedSpan>
                      </Typography>
                    </Box>
                  ) : (
                    <CouponInput
                      placeholder="Enter coupon code"
                      variant="outlined"
                      size="small"
                      fullWidth
                      value={couponCode}
                      onChange={(e) => {
                        setCouponCode(e.target.value);
                        setCouponValidationStatus(null);
                        setCouponFailureReason(null);
                      }}
                    />
                  )}
                  {couponValidationStatus !== 'valid' && (
                    <Button
                      onClick={() => {
                        onApplyRemoveCoupon(false);
                      }}
                      disabled={couponCode.trim().length === 0}
                      size="small"
                    >
                      Apply
                    </Button>
                  )}
                  {couponValidationStatus === 'valid' && (
                    <IconButton
                      onClick={() => {
                        onApplyRemoveCoupon(true);
                      }}
                      sx={{ padding: '4px' }}
                    >
                      <CloseRoundedIcon
                        sx={{ fontSize: '16px', color: 'text.primary' }}
                      />
                    </IconButton>
                  )}
                </CouponInputContainer>
                {couponFailureReason && (
                  <Typography sx={{ color: 'error.main', marginTop: '8px' }}>
                    {couponFailureReason}
                  </Typography>
                )}
                {couponValidationStatus === 'valid' && (
                  <CouponDiscountTag>
                    <Typography
                      sx={{ color: 'success.main', fontSize: '12px' }}
                    >
                      You saved ₹{cartDetails?.coupon_discount} using{' '}
                      {couponCode}
                    </Typography>
                  </CouponDiscountTag>
                )}
              </Stack>
            )}
            {variant === 'checkout' && !isMandateCart && (
              <Box sx={{ my: '4px' }}>
                <GemsDiscountToggle
                  gemsToApply={gemsToApply}
                  checked={useGems}
                  disabled={disabled}
                  onChange={setUseGems}
                />
              </Box>
            )}
            <Stack sx={{ marginTop: '10px', gap: '10px' }}>
              <Stack
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: '14px',
                  py: '12px',
                  borderRadius: '12px',
                  bgcolor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography
                  sx={{
                    fontWeight: '800',
                    fontSize: '16px',
                    color: 'text.primary',
                  }}
                >
                  Cart Total (Rs)
                </Typography>
                <Typography
                  sx={{
                    fontSize: '22px',
                    color: 'brand.goldInk',
                    fontWeight: '800',
                    fontFamily: 'guidedCourse.fontDisplay',
                  }}
                >
                  {`₹${displayedNetCurrency}`}
                </Typography>
              </Stack>
              {gemsInPlay && (
                <Stack
                  sx={{ flexDirection: 'row', justifyContent: 'space-between' }}
                >
                  <Typography
                    sx={{
                      fontWeight: '600',
                      fontSize: '16px',
                      color: 'text.primary',
                    }}
                  >
                    Cart Total (Gems)
                  </Typography>
                  <Stack
                    sx={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img src={GemsIcon} height="22px" />
                    <Typography
                      sx={{
                        fontWeight: '600',
                        fontSize: '16px',
                        color: 'text.primary',
                      }}
                    >
                      {`${netGems + (useGems ? gemsToApply : 0)}`}
                    </Typography>
                  </Stack>
                </Stack>
              )}
            </Stack>
          </>
        )}

        <Stack sx={{ gap: '5px' }}>
          {insufficientGems && (
            <Typography sx={{ color: 'red', textAlign: 'center' }}>
              Insufficient Gems
            </Typography>
          )}
          {/* What the buyer is agreeing to, as a two-step timeline rather than
              a paragraph: the only questions that matter here are "what leaves
              my account today" and "what happens after that", and a date plus
              two figures answers both faster than four sentences. */}
          {isMandateCart && variant === 'checkout' && !freeMonthMode && (
            <Box
              sx={{
                mb: '4px',
                p: '11px 13px',
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            >
              <Stack sx={{ gap: '7px' }}>
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center', gap: '8px' }}
                >
                  <CheckCircleRoundedIcon
                    sx={{ fontSize: 17, color: 'success.main' }}
                  />
                  <Typography
                    sx={{ fontSize: '13px', fontWeight: 600, flex: 1 }}
                  >
                    Today
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      fontWeight: 800,
                      color: 'success.main',
                    }}
                  >
                    {mandateFreeCycles > 0 ? 'FREE' : `₹${netCurrency}`}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center', gap: '8px' }}
                >
                  <EventRepeatRoundedIcon
                    sx={{ fontSize: 17, color: 'text.secondary' }}
                  />
                  <Typography
                    sx={{ fontSize: '13px', color: 'text.secondary', flex: 1 }}
                  >
                    {mandateFreeCycles > 0
                      ? `From ${firstChargeLabel}`
                      : 'Then every month'}
                  </Typography>
                  <Stack
                    direction="row"
                    sx={{ alignItems: 'baseline', gap: '5px' }}
                  >
                    {hasMandateDiscount && (
                      <Typography
                        sx={{
                          fontSize: '11.5px',
                          color: 'text.disabled',
                          textDecoration: 'line-through',
                        }}
                      >
                        ₹{mandateListTotal}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: '13px', fontWeight: 800 }}>
                      ₹{netCurrency}/mo
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
              <Typography
                sx={{
                  mt: '8px',
                  pt: '7px',
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  fontSize: '11px',
                  color: 'text.secondary',
                }}
              >
                Cancel anytime
                {mandateMonthsCovered > 0 &&
                  ` · up to ${mandateMonthsCovered} months`}
                {hasMandateDiscount && ' · this price is locked'}
                {mandateFreeCycles > 0 &&
                  ' · a small bank verification charge is auto-refunded'}
              </Typography>
            </Box>
          )}
          <CheckoutCta
            disabled={disabled || insufficientGems}
            onClick={() => onContinue?.(useGems && gemsToApply > 0)}
            type="submit"
          >
            {variant === 'cart'
              ? 'Checkout Now'
              : effectiveGemsOnly
                ? 'Pay with Gems'
                : isMandateCart
                  ? mandateFreeDays
                    ? `Start my ${mandateFreeDays}-day free trial`
                    : mandateFreeCycles > 0
                      ? `Start my free ${freeCycleWord(mandateFreeCycles)}`
                      : 'Start subscription'
                  : 'Continue to payment'}
          </CheckoutCta>
          {freeMonthMode && <FreeMonthNextStep />}
          {variant === 'checkout' && !effectiveGemsOnly && (
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{ fontWeight: '400', textAlign: 'center', mr: 1 }}
              >
                Secured with
              </Typography>
              <RazorpayMark src={RazorPayIcon} alt="Razorpay" />
            </Stack>
          )}
        </Stack>
      </CartCheckoutContainer>
    </Stack>
  );
};

export default PricingDetails;
